import React, { useEffect, useRef, useState } from "react";
import { QrCode, Camera, CheckCircle2, XCircle, Loader2, Search, CameraOff } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { api, API_URL } from "@/api/client";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";

function extractId(text) {
  if (!text) return "";
  const m = text.match(/\/verify\/([^/?#]+)/i);
  return (m ? m[1] : text).trim().toUpperCase();
}

export default function Verify() {
  const { t } = useI18n();
  const [manual, setManual] = useState("");
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [camError, setCamError] = useState(false);
  const scannerRef = useRef(null);

  const check = async (id) => {
    const mid = extractId(id);
    if (!mid) return;
    setChecking(true);
    setResult(null);
    try {
      const r = await api(`/verify/${encodeURIComponent(mid)}`, { skipAuth: true });
      setResult(r);
    } catch {
      setResult({ valid: false, reason: "not_found", membership_id: mid });
    } finally {
      setChecking(false);
    }
  };

  const stopScan = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); await scannerRef.current.clear(); } catch { /* scanner already stopped/unmounted */ }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const startScan = async () => {
    setCamError(false);
    setScanning(true);
    setResult(null);
    // wait for the reader div to mount
    setTimeout(async () => {
      try {
        const scanner = new Html5Qrcode("qr-reader");
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: 240 },
          async (decoded) => {
            await stopScan();
            check(decoded);
          },
          () => {}
        );
      } catch {
        setCamError(true);
        setScanning(false);
      }
    }, 100);
  };

  useEffect(() => () => { stopScan(); }, []);

  const badge = result && (
    result.valid
      ? { icon: CheckCircle2, color: "#2E7D32", bg: "#E8F5E9", label: t("verifyValid") }
      : result.reason === "expired"
        ? { icon: XCircle, color: "#F57F17", bg: "#FFF3E0", label: t("verifyExpired") }
        : result.reason === "deactivated"
          ? { icon: XCircle, color: "#9E2B25", bg: "#F0EDEA", label: t("verifyDeactivated") }
          : result.reason === "not_found"
            ? { icon: XCircle, color: "#C62828", bg: "#FDECEA", label: t("verifyNotFound") }
            : { icon: XCircle, color: "#C62828", bg: "#FDECEA", label: t("verifyInvalid") }
  );

  return (
    <div className="mx-auto max-w-md px-4 py-10">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand text-white"><QrCode size={22} /></span>
        <div>
          <h1 className="text-2xl font-black text-ink">{t("verifyTitle")}</h1>
          <p className="text-sm text-ink-muted">{t("verifyIntro")}</p>
        </div>
      </div>

      {/* Scanner */}
      <div className="mt-6 rounded-3xl border border-line bg-white p-5">
        {scanning ? (
          <>
            <div id="qr-reader" className="mx-auto w-full overflow-hidden rounded-xl" />
            <Button variant="outline" onClick={stopScan} className="mt-4 w-full rounded-full font-bold">
              <CameraOff size={16} className="mr-2" /> Stop
            </Button>
          </>
        ) : (
          <>
            <Button data-testid="verify-scan-btn" onClick={startScan} className="h-12 w-full rounded-full bg-brand font-bold hover:bg-brand-dark">
              <Camera size={18} className="mr-2" /> {t("verifyEnable")}
            </Button>
            <p className="mt-2 text-center text-xs text-ink-muted">{t("verifyWebHint")}</p>
            {camError && <p className="mt-2 text-center text-xs text-[#C62828]">{t("verifyDeniedMsg")}</p>}
          </>
        )}
      </div>

      {/* Manual */}
      <div className="mt-4 rounded-3xl border border-line bg-white p-5">
        <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-ink-soft">{t("verifyManual")}</label>
        <div className="flex gap-2">
          <input
            data-testid="verify-manual-input"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            placeholder="VHRP-2026-XXXXXXXX"
            className="w-full rounded-lg border border-line px-3 py-2.5 text-sm outline-none focus:border-brand"
            onKeyDown={(e) => e.key === "Enter" && check(manual)}
          />
          <Button data-testid="verify-check-btn" onClick={() => check(manual)} disabled={checking} className="rounded-lg bg-brand px-4 font-bold hover:bg-brand-dark">
            {checking ? <Loader2 className="animate-spin" size={18} /> : <Search size={18} />}
          </Button>
        </div>
      </div>

      {/* Result */}
      {result && badge && (
        <div className="mt-4 rounded-3xl border border-line bg-white p-6 text-center" data-testid="verify-result">
          <div className="mx-auto inline-flex items-center gap-2 rounded-full px-4 py-2 font-bold" style={{ background: badge.bg, color: badge.color }}>
            <badge.icon size={20} /> {badge.label}
          </div>
          <div className="mt-4 text-left text-sm">
            {result.photo_url && (
              <img
                src={`${API_URL}${result.photo_url}`}
                alt={result.name || "Member"}
                data-testid="verify-photo"
                className="mx-auto mb-4 h-32 w-28 rounded-xl border-2 object-cover"
                style={{ borderColor: badge.color }}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            )}
            <Row label={t("dMembershipId")} value={result.membership_id} />
            {result.name && <Row label={t("dFullName")} value={result.name} />}
            {result.designation && <Row label={t("regDesignation")} value={result.designation} />}
            {result.tier && <Row label={t("dTier")} value={`₹${result.tier} ${result.tier_label || ""}`} />}
            {result.valid_until && <Row label={t("cardValidUntil")} value={String(result.valid_until).slice(0, 10)} />}
          </div>
          <Button variant="outline" onClick={() => { setResult(null); setManual(""); }} className="mt-5 w-full rounded-full font-bold">
            {t("verifyScanAgain")}
          </Button>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3 border-b border-line py-1.5">
      <span className="text-ink-muted">{label}</span>
      <span className="font-semibold text-ink">{value || "—"}</span>
    </div>
  );
}
